import { Job, SupabaseJob, statusToDbFormat } from '@applicator/shared'

import { supabase } from '../../lib/supabase'

import { UserRules } from './types'
import { mapSupabaseToJob } from './utils'

interface HelpersParams {
  jobs: Job[]
  supabaseJobs: SupabaseJob[]
  selectedRows: Set<number>
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  setDeleteConfirm: React.Dispatch<React.SetStateAction<{
    isOpen: boolean
    type: 'single' | 'bulk'
    message: string
    jobIds: string[]
    rowIndex?: number
  } | null>>
  deleteConfirm: {
    isOpen: boolean
    type: 'single' | 'bulk'
    message: string
    jobIds: string[]
    rowIndex?: number
  } | null
  userRules: UserRules
  handleAnalyzeRelevance: (jobId: string, title: string, company: string, location: string) => Promise<void>
  searchQueryId?: string | null
  loadJobs?: () => Promise<void>
}

export const createHelpers = ({
  jobs,
  supabaseJobs,
  selectedRows,
  setJobs,
  setSupabaseJobs,
  setSelectedRows,
  setError,
  setDeleteConfirm,
  deleteConfirm,
  userRules,
  handleAnalyzeRelevance,
  _searchQueryId,
  loadJobs,
}: HelpersParams) => {
  const handleAddJob = async () => {
    const { data, error } = await supabase
      .from('jobs')
      .insert({
        title: 'New job',
        company: '',
        status: 'bookmarked',
      })
      .select()
      .single()

    if (error) {
      setError(error.message)
      return
    }

    if (data) {
      const newJob = mapSupabaseToJob(data, userRules)
      setJobs([newJob, ...jobs])
    }
  }

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) {
      return
    }

    // Get selected jobs
    const selectedJobs = Array.from(selectedRows)
      .map(idx => jobs[idx])
      .filter(job => job.id)

    if (selectedJobs.length === 0) {
      return
    }

    const jobIds = selectedJobs.map(job => job.id!).filter(Boolean)

    // Show confirmation dialog
    setDeleteConfirm({
      isOpen: true,
      type: 'bulk',
      jobIds,
      message: `Are you sure you want to delete ${selectedJobs.length} job(s)?`,
    })
  }

  const confirmBulkDelete = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'bulk' || !deleteConfirm.jobIds) return

    const jobIds = deleteConfirm.jobIds
    const selectedIndices = Array.from(selectedRows)
    const originalJobs = jobs // Save for potential revert
    const originalSupabaseJobs = supabaseJobs // Save for potential revert

    // Optimistic update
    const updatedJobs = jobs.filter((_, idx) => !selectedIndices.includes(idx))
    setJobs(updatedJobs)
    
    // Also remove from supabaseJobs
    const updatedSupabaseJobs = supabaseJobs.filter(j => jobIds.includes(j.id))
    setSupabaseJobs(updatedSupabaseJobs)
    
    setSelectedRows(new Set())

    setDeleteConfirm(null)

    // Delete from Supabase
    const { error } = await supabase
      .from('jobs')
      .delete()
      .in('id', jobIds)

    if (error) {
      // Revert on error
      setJobs(originalJobs)
      setSupabaseJobs(originalSupabaseJobs)
      setError(error.message)
    }
  }

  const confirmDeleteJob = async () => {
    console.log('[confirmDeleteJob] Called with deleteConfirm:', deleteConfirm)
    if (!deleteConfirm || deleteConfirm.type !== 'single' || deleteConfirm.rowIndex === undefined || deleteConfirm.rowIndex === null) {
      console.warn('[confirmDeleteJob] Invalid deleteConfirm state:', deleteConfirm)
      return
    }

    const rowIndex = deleteConfirm.rowIndex
    // Save jobIds before clearing deleteConfirm state
    const jobIds = deleteConfirm.jobIds || []
    const originalJobs = jobs // Save for potential revert
    const originalSupabaseJobs = supabaseJobs // Save for potential revert
    const jobToDelete = jobs[rowIndex]
    
    console.log('[confirmDeleteJob] Deleting job:', { rowIndex, jobIds, jobTitle: jobToDelete?.position })

    // Optimistic update - remove from both jobs and supabaseJobs
    const updatedJobs = jobs.filter((_, idx) => idx !== rowIndex)
    setJobs(updatedJobs)
    
    // Also remove from supabaseJobs
    if (jobToDelete?.id) {
      const updatedSupabaseJobs = supabaseJobs.filter(j => j.id !== jobToDelete.id)
      setSupabaseJobs(updatedSupabaseJobs)
    }
    
    // Remove from selected rows if it was selected
    const newSelected = new Set(selectedRows)
    newSelected.delete(rowIndex)
    // Adjust indices for rows after deleted one
    const adjustedSelected = new Set<number>()
    newSelected.forEach(idx => {
      if (idx < rowIndex) {
        adjustedSelected.add(idx)
      } else if (idx > rowIndex) {
        adjustedSelected.add(idx - 1)
      }
    })
    setSelectedRows(adjustedSelected)

    setDeleteConfirm(null)

    // Delete from Supabase
    if (jobIds.length > 0) {
      console.log('[confirmDeleteJob] Deleting from Supabase, jobId:', jobIds[0])
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobIds[0])

      if (error) {
        console.error('[confirmDeleteJob] Error deleting job:', error)
        // Revert on error
        setJobs(originalJobs)
        setSupabaseJobs(originalSupabaseJobs)
        setError(error.message)
      } else {
        console.log('[confirmDeleteJob] Job deleted successfully')
      }
    } else {
      console.warn('[confirmDeleteJob] No jobIds to delete')
    }
  }

  const handleBulkAnalyzeRelevance = async () => {
    if (selectedRows.size === 0) {
      return
    }

    // Get selected jobs that don't have relevance set
    const selectedJobs = Array.from(selectedRows)
      .map(idx => jobs[idx])
      .filter(job => job.id)
      .filter(job => {
        const supabaseJob = supabaseJobs.find(j => j.id === job.id)
        return supabaseJob && (supabaseJob.relevance_verdict === null || supabaseJob.relevance_verdict === undefined)
      })

    if (selectedJobs.length === 0) {
      alert('No jobs selected or all selected jobs already have relevance analysis.')
      return
    }

    // Start analyzing all selected jobs in parallel
    for (const job of selectedJobs) {
      if (job.id) {
        handleAnalyzeRelevance(
          job.id,
          job.position,
          job.company,
          job.location
        )
      }
    }
  }

  const handleAddToJobs = async (rowIndex: number) => {
    const job = jobs[rowIndex]
    if (!job.id) {
      setError('Job ID not found')
      return
    }

    // Remove search_query_id and set status to "Applying" to move job from search query results to general jobs list
    const { error } = await supabase
      .from('jobs')
      .update({ 
        search_query_id: null,
        status: statusToDbFormat('Applying')
      })
      .eq('id', job.id)

    if (error) {
      setError(error.message)
      return
    }

    // Remove job from current list (since it's no longer part of search query)
    const updatedJobs = jobs.filter((_, idx) => idx !== rowIndex)
    setJobs(updatedJobs)
    
    // Also remove from supabaseJobs
    const updatedSupabaseJobs = supabaseJobs.filter(j => j.id !== job.id)
    setSupabaseJobs(updatedSupabaseJobs)

    // Reload jobs if loadJobs function is provided
    if (loadJobs) {
      await loadJobs()
    }
  }

  const handleRejectRelevant = async (rowIndex: number, reason: string) => {
    const job = jobs[rowIndex]
    if (!job.id) {
      setError('Job ID not found')
      return
    }

    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      setError('Rejection reason is required')
      return
    }

    const { error } = await supabase
      .from('jobs')
      .update({
        rejected_from_relevant: true,
        rejected_reason: trimmedReason,
        rejected_at: new Date().toISOString(),
      })
      .eq('id', job.id)

    if (error) {
      setError(error.message)
      return
    }

    // Remove job from current list (since it's now rejected)
    const updatedJobs = jobs.filter((_, idx) => idx !== rowIndex)
    setJobs(updatedJobs)

    // Also remove from supabaseJobs
    const updatedSupabaseJobs = supabaseJobs.filter(j => j.id !== job.id)
    setSupabaseJobs(updatedSupabaseJobs)

    // Reload jobs if loadJobs function is provided
    if (loadJobs) {
      await loadJobs()
    }
  }

  const handleBulkAddToJobs = async () => {
    if (selectedRows.size === 0) {
      return
    }

    // Get selected jobs and filter only relevant ones
    const selectedJobs = Array.from(selectedRows)
      .map(idx => {
        const job = jobs[idx]
        if (!job.id) return null
        const supabaseJob = supabaseJobs.find(j => j.id === job.id)
        const isRelevant = supabaseJob?.relevance_verdict === 'Fit' || supabaseJob?.is_relevant === true
        return isRelevant ? job : null
      })
      .filter((job): job is Job => job !== null && job.id !== undefined)

    if (selectedJobs.length === 0) {
      setError('No relevant jobs selected. Please select only relevant jobs to add to your Jobs list.')
      return
    }

    const jobIds = selectedJobs.map(job => job.id!).filter(Boolean)

    // Remove search_query_id and set status to "Applying" for all selected relevant jobs
    const { error } = await supabase
      .from('jobs')
      .update({ 
        search_query_id: null,
        status: statusToDbFormat('Applying')
      })
      .in('id', jobIds)

    if (error) {
      setError(error.message)
      return
    }

    // Remove selected jobs from current list
    const selectedIndices = Array.from(selectedRows)
    const updatedJobs = jobs.filter((_, idx) => !selectedIndices.includes(idx))
    setJobs(updatedJobs)
    
    // Also remove from supabaseJobs
    const updatedSupabaseJobs = supabaseJobs.filter(j => !jobIds.includes(j.id))
    setSupabaseJobs(updatedSupabaseJobs)
    
    // Clear selection
    setSelectedRows(new Set())

    // Reload jobs if loadJobs function is provided
    if (loadJobs) {
      await loadJobs()
    }
  }

  return {
    handleAddJob,
    handleBulkDelete,
    confirmBulkDelete,
    confirmDeleteJob,
    handleBulkAnalyzeRelevance,
    handleAddToJobs,
    handleRejectRelevant,
    handleBulkAddToJobs,
  }
}
